import React, { useCallback, useEffect, useRef, useState } from 'react';

import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import {
  fetch_rule_change,
  post_rule_apply,
  post_rule_approve,
  post_rule_cancel_approval,
  post_rule_preview,
  post_rule_reject,
  post_rule_request_approval,
  post_rule_withdraw
} from './api';
import {
  APPROVAL_DONE_TEXT,
  apply_state,
  can_discard,
  can_rerun,
  confirm_copy,
  change_status_view,
  change_version_name,
  changed_fields_text,
  day_label,
  local_refusal,
  month_text,
  preview_running,
  preview_status_text,
  progress_view,
  refusal,
  save_after_approval,
  save_deadline,
  saved_text,
  stamp_ist,
  today_warning
} from './logic';
import PreviewSummary from './preview_summary';
import PreviewLines from './preview_lines';
import ApprovalCard, { Confirm } from './approval_card';

/*
 * one rule change: what it changes, its dry-run preview (polled while it runs), the impact, its
 * approval step when it needs one, and "Save change".
 */
const RefusalBox = ({ r, on_restart }) => (
  <div className="tp-banner tp-banner-error" role="alert">
    <div>{r.text}</div>
    {r.approval_reasons.length ? (
      <ul className="tp-rx-cp-list">
        {r.approval_reasons.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
    ) : null}
    {r.problems.length ? (
      <ul className="tp-rx-cp-list">
        {r.problems.map((p, i) => (
          // eslint-disable-next-line react/no-array-index-key -- problems have no id and never reorder
          <li key={i}>{p.message}</li>
        ))}
      </ul>
    ) : null}
    {r.code === 'RULES_CHANGED' && on_restart ? (
      <button type="button" className="tp-link" onClick={on_restart}>
        Reload the rules
      </button>
    ) : null}
  </div>
);

const ChangePanel = ({
  change_uuid,
  initial = null,
  names = {},
  catalogue,
  now = () => new Date(),
  poll_ms = 3000,
  on_saved,
  on_changed,
  on_rules_changed,
  on_busy,
  on_error,
  on_prepare_again,
  can_prepare_again
}) => {
  const [change, setChange] = useState(initial);
  const [load_error, setLoadError] = useState('');
  const [rev, setRev] = useState(initial ? 0 : 1);
  const [busy, setBusy] = useState('');
  const [failure, setFailure] = useState(null);
  const [saved, setSaved] = useState('');
  // Bumped on every answer, so the next poll waits for this one even if the view is unchanged.
  const [answers, setAnswers] = useState(0);
  const [confirm_discard, setConfirmDiscard] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (on_busy) on_busy(!!busy);
  }, [busy, on_busy]);

  // rev > 0 asks for a read: the first load without an initial view, a poll tick, or a retry.
  useEffect(() => {
    if (!rev) return undefined;
    let cancelled = false;
    fetch_rule_change(change_uuid)
      .then((data) => {
        if (cancelled) return;
        setChange(data);
        setLoadError('');
        setAnswers((n) => n + 1);
      })
      .catch((e) => {
        if (cancelled) return;
        const r = refusal(e, 'Could not load this rule change.');
        if (r.permission && on_error) on_error(e, r.text);
        setLoadError(r.text);
      });
    return () => {
      cancelled = true;
    };
  }, [change_uuid, rev, on_error]);

  const running = !!change && preview_running(change.preview);
  // A failed poll stops the polling; "Try again" resumes it.
  useEffect(() => {
    if (!running || load_error) return undefined;
    const t = setTimeout(() => setRev((r) => r + 1), poll_ms);
    return () => clearTimeout(t);
  }, [running, answers, load_error, poll_ms]);

  const write = useCallback(
    (kind, call, fallback) => {
      seq.current += 1;
      const s = seq.current;
      setBusy(kind);
      setFailure(null);
      setSaved('');
      return call()
        .then((data) => {
          if (s !== seq.current) return;
          setChange(data);
          setAnswers((n) => n + 1);
          if ((kind === 'apply' || kind === 'approve_save') && data.status === 'APPLIED') {
            setSaved(saved_text(data));
            if (on_saved) on_saved(data);
          }
          if (APPROVAL_DONE_TEXT[kind] && !data.replayed) setSaved(APPROVAL_DONE_TEXT[kind]);
          // Approve and save stopped after the approval (the save was not possible): say what did land.
          if (kind === 'approve_save' && data.status === 'APPROVED' && !data.replayed)
            setSaved(APPROVAL_DONE_TEXT.approve);
          if (on_changed) on_changed(data);
        })
        .catch((e) => {
          if (s !== seq.current) return;
          const r = refusal(e, fallback);
          if (r.permission && on_error) on_error(e, r.text);
          // Approve and save: the approval landed, only the save did not.
          if (e && e.approved_first) {
            r.text = `Approved, but not saved yet. ${r.text}`;
            if (on_changed) on_changed(null);
          }
          setFailure(r);
          // The refusal may come from state that moved (stale, blocked, rules changed): re-read the change.
          if (!r.dropped) setRev((x) => x + 1);
        })
        .finally(() => {
          if (s === seq.current) setBusy('');
        });
    },
    [on_saved, on_changed, on_error]
  );

  if (!change) {
    return load_error ? (
      <LoadFailed text={load_error} on_retry={() => setRev((r) => r + 1)} />
    ) : (
      <div className="tp-empty" role="status">
        Loading the rule change…
      </div>
    );
  }

  const p = change.preview;
  const status = change_status_view(change.status);
  const apply = apply_state(change, now());
  const progress = progress_view(p);
  const months_text = p && Array.isArray(p.months) ? p.months.map(month_text).join(', ') : '';

  const do_apply = () => {
    // The clock may have moved since this rendered; never send a request the 24-hour rule refuses.
    const st = apply_state(change, now());
    if (!st.enabled) {
      setSaved('');
      setFailure(local_refusal(st.reason || 'This change cannot be saved yet.'));
      return;
    }
    write(
      'apply',
      () => post_rule_apply(change.change_uuid, p.preview_uuid),
      'Could not save the change. Try again.'
    );
  };
  const do_rerun = () =>
    write(
      'preview',
      () => post_rule_preview(change.change_uuid),
      'Could not start the preview. Try again.'
    );
  // The approval names the preview on screen, so a newer one the reader has not seen is refused.
  const shown_preview = (p && p.preview_uuid) || '';
  const approval_actions = {
    on_request: (note) =>
      write(
        'request',
        () => post_rule_request_approval(change.change_uuid, shown_preview, note),
        'Could not send the change for approval. Try again.'
      ),
    on_approve: (note) =>
      write(
        'approve',
        () => post_rule_approve(change.change_uuid, shown_preview, note),
        'Could not approve the change. Try again.'
      ),
    // One action for the approver: approve this preview, then save it if nothing blocks it now.
    on_approve_and_save: (note) =>
      write(
        'approve_save',
        () =>
          post_rule_approve(change.change_uuid, shown_preview, note).then((approved) => {
            if (!save_after_approval(approved, now())) return approved;
            return post_rule_apply(approved.change_uuid, approved.preview.preview_uuid).catch(
              (e) => {
                const err = e || {};
                err.approved_first = true;
                throw err;
              }
            );
          }),
        'Could not approve the change. Try again.'
      ),
    on_reject: (note) =>
      write(
        'reject',
        () => post_rule_reject(change.change_uuid, note),
        'Could not reject the change. Try again.'
      ),
    on_withdraw: () =>
      write(
        'withdraw',
        () => post_rule_withdraw(change.change_uuid, ''),
        'Could not withdraw the request. Try again.'
      ),
    on_cancel_approval: () =>
      write(
        'cancel_approval',
        () => post_rule_cancel_approval(change.change_uuid, ''),
        'Could not cancel the approval. Try again.'
      )
  };
  const do_discard = () => {
    setConfirmDiscard(false);
    write(
      'discard',
      () => post_rule_withdraw(change.change_uuid, ''),
      'Could not discard the draft. Try again.'
    );
  };
  const deadline = save_deadline(change, now());
  const today_text = today_warning(change, now());
  const open = ['PREVIEWED', 'PENDING_APPROVAL', 'APPROVED'].includes(change.status);

  return (
    <div className="tp-rx-change">
      <div className="tp-rx-head">
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
        <span>
          Takes effect <span className="tp-strong">{day_label(change.effective_date)}</span>, 00:00
          IST
        </span>
      </div>
      <p className="tp-rx-cp-line">
        <span className="tp-strong">Why: </span>
        {change.note}
      </p>
      {change.teacher_notice ? (
        <p className="tp-rx-cp-line">
          <span className="tp-strong">Teacher notice: </span>
          {change.teacher_notice}
        </p>
      ) : null}
      {change.no_notice_reason ? (
        <p className="tp-rx-cp-line">
          <span className="tp-strong">No teacher notice, because: </span>
          {change.no_notice_reason}
        </p>
      ) : null}
      <p className="tp-hint tp-rx-cp-by">
        Prepared by {(change.requested_by && change.requested_by.name) || '—'}
        {change.created_at_ist ? ` on ${stamp_ist(change.created_at_ist)}` : ''}
        {change.applied_by
          ? `. Saved by ${change.applied_by.name} on ${stamp_ist(change.applied_at_ist)}`
          : ''}
        .
      </p>

      <section className="tp-card" aria-labelledby="tp-rx-versions">
        <div className="tp-card-hd">
          <span className="tp-card-hd-title" id="tp-rx-versions">
            What changes
          </span>
          {change.wording_only ? (
            <span className="tp-card-hd-sub">Wording only: no payment moves</span>
          ) : null}
        </div>
        {(change.versions || []).map((v) => (
          <div key={v.rule_key} className="tp-card-item tp-rx-cp-ver">
            <div className="tp-strong">{change_version_name(v, names)}</div>
            {v.before ? (
              <div>
                <span className="tp-card-field-label">Before: </span>
                {v.before.sentence}
              </div>
            ) : (
              <div className="tp-hint">A new rule.</div>
            )}
            <div>
              <span className="tp-card-field-label">After: </span>
              {v.after && v.after.sentence}
            </div>
            {v.changed_fields && v.changed_fields.length ? (
              <div className="tp-hint">Changed: {changed_fields_text(v.changed_fields)}</div>
            ) : null}
          </div>
        ))}
      </section>

      <div className="tp-banner tp-banner-info" role="note">
        A preview is a dry run over the open months. It saves nothing and changes no payment; only
        &quot;Save change&quot; does.
      </div>

      <section className="tp-card" aria-labelledby="tp-rx-preview">
        <div className="tp-card-hd">
          <span className="tp-card-hd-title" id="tp-rx-preview">
            Preview
          </span>
          <span className="tp-card-hd-sub" role="status" aria-live="polite">
            {preview_status_text(p && p.status)}
            {p && p.status === 'DONE' && p.finished_at_ist
              ? `, ${stamp_ist(p.finished_at_ist)}`
              : ''}
          </span>
        </div>
        {running ? (
          <div className="tp-form">
            <progress
              className="tp-run-progress"
              max={Math.max(progress.total, 1)}
              value={progress.done}
              aria-label="Preview progress"
              aria-valuetext={progress.text || 'Starting'}
            />
            <div className="tp-hint">{progress.text || 'Waiting for a free preview slot…'}</div>
          </div>
        ) : null}
        {p && p.status === 'FAILED' ? (
          <div className="tp-banner tp-banner-error" role="alert">
            {p.error || 'The preview failed.'} Re-run it.
          </div>
        ) : null}
        {load_error && change ? (
          <LoadFailed
            text={`Could not refresh the preview. ${load_error}`}
            on_retry={() => setRev((r) => r + 1)}
          />
        ) : null}
        {can_rerun(change) ? (
          <div className="tp-rx-cp-rerun">
            <button type="button" className="tp-btn-out" disabled={!!busy} onClick={do_rerun}>
              {busy === 'preview' ? 'Starting…' : 'Re-run preview'}
            </button>
            {change.status === 'APPROVED' ? (
              <span className="tp-hint">
                Re-running cancels the approval: it must be approved again.
              </span>
            ) : null}
          </div>
        ) : null}
      </section>

      {p && p.status === 'DONE' && p.summary ? (
        <PreviewSummary
          summary={p.summary}
          names={names}
          catalogue={catalogue}
          months_text={months_text}
        />
      ) : null}
      {p && p.status === 'DONE' ? (
        <PreviewLines key={p.preview_uuid} preview_uuid={p.preview_uuid} catalogue={catalogue} />
      ) : null}

      <ApprovalCard change={change} busy={busy} deadline={deadline} {...approval_actions} />

      {today_text ? (
        <div className="tp-banner tp-banner-warn" role="note">
          {today_text}
        </div>
      ) : null}

      {failure ? <RefusalBox r={failure} on_restart={on_rules_changed} /> : null}
      {saved ? (
        <div className="tp-banner tp-banner-ok" role="status">
          {saved}
        </div>
      ) : null}

      {(change.status === 'REJECTED' || change.status === 'WITHDRAWN') &&
      on_prepare_again &&
      can_prepare_again &&
      can_prepare_again(change) ? (
        <div className="tp-modal-ft">
          <button type="button" className="tp-btn-out" onClick={() => on_prepare_again(change)}>
            Prepare again
          </button>
        </div>
      ) : null}

      {open ? (
        <div className="tp-modal-ft">
          {!apply.enabled && apply.reason ? (
            <span className="tp-hint" data-testid="rx-apply-reason">
              {apply.reason}
            </span>
          ) : null}
          {apply.enabled && deadline && deadline.text ? (
            <span className="tp-hint" data-testid="rx-apply-deadline">
              {deadline.text}
            </span>
          ) : null}
          {can_discard(change) ? (
            <button
              type="button"
              className="tp-btn-out"
              disabled={!!busy}
              onClick={() => setConfirmDiscard(true)}
            >
              {busy === 'discard' ? 'Discarding…' : 'Discard draft'}
            </button>
          ) : null}
          {/* Orange only once it can save: until then the approval card holds the one primary. */}
          <button
            type="button"
            className={apply.enabled ? 'tp-btn' : 'tp-btn-out'}
            disabled={!apply.enabled || !!busy}
            onClick={do_apply}
          >
            {busy === 'apply' ? 'Saving…' : 'Save change'}
          </button>
        </div>
      ) : null}
      {confirm_discard ? (
        <Confirm
          copy={confirm_copy('discard')}
          on_keep={() => setConfirmDiscard(false)}
          on_go={do_discard}
        />
      ) : null}
    </div>
  );
};

export default ChangePanel;
